import { useMemo } from 'react';
import { Stack, Group, Button, Text, Card } from '@repletoryreach/sdk-ui';
import { Alert, WhatsAppPreview } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { parseComponents } from '../../../lib/template-components';
import { formatComponents } from '../../../lib/template-validation';
import { HeaderBuilder, isMediaFormat, type HeaderFormat } from '../components/HeaderBuilder';
import { ButtonsBuilder } from './ButtonsBuilder';
import {
  appendPlaceholder, componentsFromDraft, highestPlaceholder, resizeSamples,
  type TemplateDraft
} from './draft';

const BODY_MAX = 1024;
const HEADER_MAX = 60;
const FOOTER_MAX = 60;

/**
 * Step 3: the message itself, with the handset beside it.
 *
 * Two things this replaces. The author used to edit Meta's `components` JSON
 * blind and find out what it looked like by saving and opening the detail page.
 * And a placeholder was a piece of punctuation you typed yourself, with no
 * sample value anywhere, so Meta reviewed every template against wording the
 * service invented ("Sample 1") rather than against a real example.
 */
export function ContentStep({
  draft,
  onChange,
  raw,
  onRawChange,
  mode,
  onModeChange,
  lossless
}: {
  draft: TemplateDraft;
  onChange: (next: TemplateDraft) => void;
  /** The components JSON, when editing as JSON. */
  raw: string;
  onRawChange: (next: string) => void;
  mode: 'form' | 'json';
  onModeChange: (next: 'form' | 'json') => void;
  /** False when the template carries something the form cannot show. */
  lossless: boolean;
}) {
  const t = useT();

  const components = mode === 'json' ? raw : componentsFromDraft(draft);
  const preview = useMemo(() => parseComponents(components), [components]);
  const header = preview.find((c) => c.type === 'HEADER');
  const body = preview.find((c) => c.type === 'BODY');
  const footer = preview.find((c) => c.type === 'FOOTER');
  const buttons = preview.find((c) => c.type === 'BUTTONS')?.buttons ?? [];

  const placeholders = highestPlaceholder(draft.body);
  const samples = resizeSamples(draft.bodySamples, placeholders);

  return (
    <div className="rr-tplwiz__content">
      <Stack gap="lg">
        {!lossless && mode === 'json' && (
          <Alert color="warning">{t('templates.wizard.content.advancedOnly')}</Alert>
        )}

        <Group justify="space-between" align="center">
          <Text role="inputLabel">{t('templates.wizard.content.title')}</Text>
          {/*
            The escape hatch. Everything the form offers is Meta's standard
            template; an account that already has an OTP template or a flow
            button needs somewhere to keep it, and silently dropping what a form
            cannot show is the one unforgivable failure of a form editor.
          */}
          <Button
            size="xs"
            variant="subtle"
            disabled={!lossless && mode === 'json'}
            onClick={() => onModeChange(mode === 'form' ? 'json' : 'form')}
          >
            {mode === 'form' ? t('templates.wizard.content.editJson') : t('templates.wizard.content.editForm')}
          </Button>
        </Group>

        {mode === 'json' ? (
          <Stack gap={6}>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('templates.wizard.content.jsonHint')}</Text>
            <Textarea
              value={raw}
              onChange={(e) => onRawChange(e.currentTarget.value)}
              minRows={18}
              className="rr-templates__code"
              aria-label={t('templates.wizard.content.json')}
            />
            <Group justify="flex-end">
              <Button size="xs" variant="subtle" onClick={() => onRawChange(formatComponents(raw))}>
                {t('templates.drawer.format')}
              </Button>
            </Group>
          </Stack>
        ) : (
          <>
            <HeaderBuilder
              format={draft.headerFormat}
              text={draft.headerText}
              sampleUrl={draft.headerSampleUrl}
              onChange={(next: { format: HeaderFormat; text: string; sampleUrl: string }) =>
                onChange({
                  ...draft,
                  headerFormat: next.format,
                  headerText: next.text,
                  headerSampleUrl: next.sampleUrl
                })}
            />

            {/* A text header may carry one placeholder of its own, numbered
                separately from the body's, and it needs its own example. */}
            {draft.headerFormat === 'TEXT' && highestPlaceholder(draft.headerText) > 0 && (
              <TextInput
                label={t('templates.wizard.header.sample')}
                description={t('templates.wizard.header.sample.hint')}
                value={draft.headerSample}
                onChange={(e) => onChange({ ...draft, headerSample: e.currentTarget.value })}
              />
            )}

            <Stack gap={6}>
              <Group justify="space-between" align="center">
                <Text role="inputLabel">{t('templates.wizard.body')}</Text>
                <Group gap={8} align="center">
                  <Text role="caption" c={draft.body.length > BODY_MAX ? 'var(--rr-color-error)' : 'var(--rr-color-text-muted)'}>
                    {t('templates.wizard.count', { length: draft.body.length, max: BODY_MAX })}
                  </Text>
                  <Button size="xs" variant="default" onClick={() => onChange(appendPlaceholder(draft))}>
                    {t('templates.wizard.addPlaceholder')}
                  </Button>
                </Group>
              </Group>
              <Textarea
                value={draft.body}
                onChange={(e) => onChange({
                  ...draft,
                  body: e.currentTarget.value,
                  bodySamples: resizeSamples(draft.bodySamples, highestPlaceholder(e.currentTarget.value))
                })}
                minRows={6}
                placeholder={t('templates.wizard.body.placeholder')}
                aria-label={t('templates.wizard.body')}
              />
              <Text role="caption" c="var(--rr-color-text-muted)">{t('templates.wizard.body.hint')}</Text>
            </Stack>

            {/*
              One sample per placeholder. Meta reviews the template against
              these, so an empty one means the review reads "Sample 1" where the
              customer's name will be.
            */}
            {placeholders > 0 && (
              <Card withBorder className="rr-tplwiz__samples">
                <Stack gap={8}>
                  <Text role="inputLabel">{t('templates.wizard.samples')}</Text>
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('templates.wizard.samples.hint')}</Text>
                  {samples.map((s, i) => (
                    <TextInput
                      key={i}
                      label={t('templates.wizard.samples.for', { n: i + 1 })}
                      value={s}
                      placeholder={t('templates.wizard.samples.placeholder')}
                      onChange={(e) => {
                        const next = samples.slice();
                        next[i] = e.currentTarget.value;
                        onChange({ ...draft, bodySamples: next });
                      }}
                    />
                  ))}
                </Stack>
              </Card>
            )}

            <TextInput
              label={t('templates.wizard.footer')}
              description={t('templates.wizard.footer.hint')}
              value={draft.footer}
              maxLength={FOOTER_MAX}
              onChange={(e) => onChange({ ...draft, footer: e.currentTarget.value })}
            />

            <ButtonsBuilder
              buttons={draft.buttons}
              onChange={(next) => onChange({ ...draft, buttons: next })}
            />
          </>
        )}
      </Stack>

      {/* The handset, live. */}
      <div className="rr-tplwiz__preview">
        <Text role="inputLabel">{t('templates.drawer.preview')}</Text>
        <WhatsAppPreview
          chrome
          businessName={t('templates.wizard.preview.business')}
          composerLabel={t('templates.wizard.preview.composer')}
          header={header
            ? {
              text: header.text,
              format: header.format,
              mediaUrl: isMediaFormat(header.format) ? header.example?.header_handle?.[0] : undefined
            }
            : null}
          body={body?.text}
          footer={footer?.text}
          buttons={buttons}
          emptyBodyLabel={t('templates.wizard.preview.empty')}
          renderPlaceholder={(position) => (
            <span className="rr-tplwiz__ph">
              {samples[Number(position) - 1]?.trim() || `{{${position}}}`}
            </span>
          )}
        />
        <Text role="caption" c="var(--rr-color-text-muted)">{t('templates.wizard.preview.hint')}</Text>
      </div>
    </div>
  );
}

export const CONTENT_LIMITS = { BODY_MAX, HEADER_MAX, FOOTER_MAX };
